import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import ScopeField from '../../components/ScopeField';
import LotteryCodeField from '../../components/LotteryCodeField';
import DateField from '../../components/DateField';
import apiRequest from '../../api/client';
import { todayISO } from '../../utils/date';
import './ListeVentesPage.css';

const SCOPES = [
  { key: 'All', label: 'Tout' },
  { key: 'Group', label: 'Groupe' },
  { key: 'Supervisor', label: 'Superviseur' },
  { key: 'Bank', label: 'Banque' },
];

const money = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Repartit en `cols` colonnes en mode "column-major" : on lit une colonne de haut
// en bas, puis la suivante — l'ordre numerique est preserve.
const columnize = (arr, cols) => {
  const rows = Math.ceil(arr.length / cols) || 1;
  const out = Array.from({ length: cols }, () => []);
  arr.forEach((item, i) => { out[Math.floor(i / rows)].push(item); });
  return out.filter((c) => c.length);
};

const fetchSalesList = ({ tirage, date, filter, scope }) => {
  const q = new URLSearchParams({ tirage, date });
  if (filter !== 'All') q.set('filter', filter);
  if (filter !== 'All' && scope) q.set('scope', scope);
  return apiRequest(`/tickets/sales-list/?${q.toString()}`);
};

const ListeVentesPage = () => {
  const [filter, setFilter] = useState('All');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [date, setDate] = useState(todayISO());
  const [lottery, setLottery] = useState('1');
  const [lotteryName, setLotteryName] = useState('NY MIDI');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const search = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      setData(await fetchSalesList({ tirage: lottery, date, filter, scope: scopeId }));
    } catch (err) {
      setError('Recherche impossible.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <p className="lv-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/rapports" className="bc-link">Rapports</Link>
        {' >> '}
        <span className="bc-current">Liste des ventes</span>
      </p>

      <div className="lv-card no-print">
        <div className="lv-card-header"><span className="lv-card-title">Liste des ventes</span></div>
        <form className="lv-form" onSubmit={search}>
          <div className="lv-radio-row">
            {SCOPES.map((s) => (
              <label key={s.key}>
                <input type="radio" name="scope" checked={filter === s.key}
                  onChange={() => { setFilter(s.key); setScopeId(''); setScopeName(''); }} />
                {s.label}
              </label>
            ))}
          </div>

          {filter !== 'All' && (
            <div className="lv-row">
              <label className="lv-label">{SCOPES.find((s) => s.key === filter).label}</label>
              <ScopeField
                filter={filter}
                id={scopeId}
                name={scopeName}
                onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                inputClass="lv-input lv-input-sm"
                iconClass="lv-icon"
                nameClass="lv-label"
              />
            </div>
          )}

          <div className="lv-row">
            <label className="lv-label">Date</label>
            <DateField className="lv-input" value={date} onChange={setDate} />
          </div>

          <div className="lv-row">
            <label className="lv-label">Loterie</label>
            <LotteryCodeField
              value={lottery}
              name={lotteryName}
              onChange={setLottery}
              onResolve={(code, name) => { setLottery(code); setLotteryName(name); }}
              inputClass="lv-input lv-input-sm"
              iconClass="lv-icon"
              nameClass="lv-lottery-name"
            />
          </div>

          {error && <p className="lv-error">{error}</p>}
          <button type="submit" className="lv-btn" disabled={loading}>{loading ? '…' : 'Chercher'}</button>
        </form>
      </div>

      {data && (
        <div className="lv-report">
          <div className="lv-report-head">
            <div className="lv-rh-title">Ventes — Liste du {data.date}</div>
            <div className="lv-rh-sub">
              <strong>{data.tirage_name}</strong> · {data.scope_label}
              {data.tirage_close && <> · fermeture {data.tirage_close}</>}
            </div>
            <div className="lv-rh-meta">Généré : {data.generated_at}</div>
            <button type="button" className="lv-print no-print" onClick={() => window.print()}>Imprimer</button>
          </div>

          {data.products.length === 0 && <p className="lv-empty">Aucune vente pour ces critères.</p>}

          {data.products.map((p) => (
            <section key={p.code} className="lv-product">
              <h3 className="lv-product-name">{p.name}</h3>
              <div className="lv-cols">
                {columnize(p.numbers, 4).map((col, ci) => (
                  <table key={ci} className="lv-col-table">
                    <thead>
                      <tr><th>N°</th><th>Vendu</th><th>×</th></tr>
                    </thead>
                    <tbody>
                      {col.map((n) => (
                        <tr key={n.numero}>
                          <td className="lv-k">{n.numero}</td>
                          <td className="lv-v">{money(n.vendu)}</td>
                          <td className="lv-p">{n.paris}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}
              </div>
              <div className="lv-product-total">
                <span>Paris : {p.total_paris}</span>
                <span>Total {p.name} : <strong>{money(p.total_vendu)}</strong></span>
              </div>
            </section>
          ))}

          <div className="lv-grand">
            <div><span>Ventes brutes</span><span>{money(data.brut)}</span></div>
            <div><span>Moins % (commission)</span><span>{money(data.commission)}</span></div>
            <div className="lv-grand-net"><span>Ventes nettes</span><span>{money(data.net)}</span></div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ListeVentesPage;
